class Carousel extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector(".js-carousel-slider");
    if (!this.slider) return;

    this.enableSliderAutoplay =
      this.getAttribute("data-slider-autoplay") === "true";
    this.autoplaySpeed = this.getAttribute("data-slider-autoplay-speed")
      ? parseFloat(this.getAttribute("data-slider-autoplay-speed"))
      : 3000;

    this.centered = this.classList.contains("carousel--center");
    this.delayInit = this.getAttribute("data-delay-init") === "true";
    this.enableLooping = this.getAttribute("data-enable-looping") !== "false";
    this.windowWidth = window.innerWidth;

    this.dots = this.querySelector(".js-carousel-dots");
    this.prevButtons = this.querySelectorAll('button[name="previous"]');
    this.nextButtons = this.querySelectorAll('button[name="next"]');
    this.trackers = this.querySelectorAll(".js-carousel-tracker");

    if (this.dataset.tabbed === "true") {
      this.tab = this.closest("carousel-tabbed");
      this.index = this.getAttribute("data-tab-index");

      this.prevButtons = this.tab.querySelectorAll(
        `.js-carousel-control-wrapper[data-tab-index="${this.index}"] button[name="previous"]`
      );
      this.nextButtons = this.tab.querySelectorAll(
        `.js-carousel-control-wrapper[data-tab-index="${this.index}"] button[name="next"]`
      );
      this.trackers = this.tab.querySelectorAll(
        `.js-carousel-control-wrapper[data-tab-index="${this.index}"] .js-carousel-tracker`
      );
    }

    this.sliderItems = [];
    this.sliderItemsToShow = [];
    this.sliderItemOffset = 0;
    this.slidesPerPage = 1;
    this.totalPages = 1;
    this.currentPage = 1;
    this.slideScrollPosition = 0;

    this.isUpdating = false;
    this.isScrolling = false;
    this.isLooping = false;

    this.scrollEndTimer = null;
    this.pendingSwipeDistance = null;
    this.boundScrollEnd = null;

    this.boundUpdate = this.update.bind(this);
    this.boundHandleResize = this.handleResize.bind(this);
    this.boundButtonClick = this.onButtonClick.bind(this);

    if (this.centered) {
      this.slider.style.minHeight = this.slider.offsetHeight + "px";
    }

    this.setupTouchHandling();

    if (this.enableSliderAutoplay) {
      this.autoplay();
    }

    if (this.delayInit) {
      this.addEventListener("trigger-init", () => this.initPages());
    } else {
      this.initPages();
    }

    this.resizeObserver = new ResizeObserver(this.boundHandleResize);
    this.resizeObserver.observe(this.slider);

    this.thumbnailSubscription = subscribe(
      PUB_SUB_EVENTS.thumbnailChange,
      (data) => this.scrollToSlide(data.index)
    );
  }

  handleResize() {
    if (this.isUpdating) return;

    this.isUpdating = true;

    window.requestAnimationFrame(() => {
      this.initPages(true);
      this.isUpdating = false;
    });
  }

  setupTouchHandling() {
    let touchStartX = 0;
    let touchEndX = 0;
    let isSwiping = false;

    this.boundTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
      isSwiping = true;
      this.cancelScrollEndDetection();
      this.pendingSwipeDistance = null;
    };

    this.boundTouchEnd = (e) => {
      if (!isSwiping) return;

      touchEndX = e.changedTouches[0].clientX;
      isSwiping = false;

      const swipeDistance = touchStartX - touchEndX;

      if (Math.abs(swipeDistance) >= 100) {
        this.pendingSwipeDistance = swipeDistance;
        this.detectScrollEnd();
      }
    };

    this.slider.addEventListener("touchstart", this.boundTouchStart, {
      passive: true,
    });
    this.slider.addEventListener("touchend", this.boundTouchEnd, {
      passive: true,
    });
  }

  cancelScrollEndDetection() {
    if (this.scrollEndTimer) {
      clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = null;
    }

    if (this.boundScrollEnd) {
      this.slider.removeEventListener("scrollend", this.boundScrollEnd);
      this.boundScrollEnd = null;
    }
  }

  detectScrollEnd() {
    if ("onscrollend" in window) {
      this.boundScrollEnd = () => {
        this.boundScrollEnd = null;

        if (this.pendingSwipeDistance !== null) {
          this.checkLoopAfterSwipe(this.pendingSwipeDistance);
          this.pendingSwipeDistance = null;
        }
      };

      this.slider.addEventListener("scrollend", this.boundScrollEnd, {
        once: true,
      });
      return;
    }

    const lastScrollLeft = this.slider.scrollLeft;

    this.scrollEndTimer = setTimeout(() => {
      this.scrollEndTimer = null;

      if (this.slider.scrollLeft === lastScrollLeft) {
        if (this.pendingSwipeDistance !== null) {
          this.checkLoopAfterSwipe(this.pendingSwipeDistance);
          this.pendingSwipeDistance = null;
        }
      } else {
        this.detectScrollEnd();
      }
    }, 100);
  }

  checkLoopAfterSwipe(swipeDistance) {
    if (!this.enableLooping) return;

    const swipeThreshold = 100;

    if (swipeDistance > swipeThreshold && this.isAtEnd()) {
      this.loopToStart();
    } else if (swipeDistance < -swipeThreshold && this.isAtStart()) {
      this.loopToEnd();
    }
  }

  initPages(afterInit = false) {
    if (this.centered && afterInit && window.innerWidth !== this.windowWidth) {
      const activeSlide = this.slider.querySelector(".js-carousel-slide.active");
      if (activeSlide) {
        this.slider.style.minHeight = activeSlide.offsetHeight + 40 + "px";
      }
      this.windowWidth = window.innerWidth;
    }

    this.sliderItems = this.querySelectorAll(".js-carousel-slide");
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );

    this.slider.removeEventListener("scroll", this.boundUpdate);
    this.slider.addEventListener("scroll", this.boundUpdate, { passive: true });

    this.bindButtonListeners();
    this.measurePages();

    this.currentPage = this.getCurrentPageFromScroll();
    this.update();

    if (this.dots) {
      this.initDots();
    }
  }

  bindButtonListeners() {
    if (this.prevButtons.length) {
      this.prevButtons.forEach((button) => {
        button.removeEventListener("click", this.boundButtonClick);
        button.addEventListener("click", this.boundButtonClick);
      });
    }

    if (this.nextButtons.length) {
      this.nextButtons.forEach((button) => {
        button.removeEventListener("click", this.boundButtonClick);
        button.addEventListener("click", this.boundButtonClick);
      });
    }
  }

  measurePages() {
    this.sliderItemOffset = 0;
    this.slidesPerPage = 1;
    this.totalPages = Math.max(1, this.sliderItemsToShow.length);

    if (!this.sliderItemsToShow.length) {
      return;
    }

    if (this.centered && this.sliderItemsToShow[0]) {
      const halfSlideWidth = this.sliderItemsToShow[0].offsetWidth / 2;
      this.sliderItemsToShow[0].style.cssText = `
        margin-left: calc(50% - ${halfSlideWidth}px);
        scroll-margin-left: calc(50% - ${halfSlideWidth}px);
      `;

      this.sliderItemsToShow[this.sliderItemsToShow.length - 1].style.cssText = `
        margin-right: calc(50% - ${halfSlideWidth}px);
        scroll-margin-right: calc(50% - ${halfSlideWidth}px);
      `;
    }

    if (this.sliderItemsToShow.length < 2) {
      this.totalPages = 1;
      this.slidesPerPage = 1;
      return;
    }

    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;

    if (!this.sliderItemOffset) {
      this.totalPages = 1;
      this.slidesPerPage = 1;
      return;
    }

    this.slidesPerPage =
      Math.floor(this.slider.clientWidth / this.sliderItemOffset) + 1;

    if (this.classList.contains("carousel--show-to-edge")) {
      this.slidesPerPage -= 1;
    }

    this.slidesPerPage = Math.max(1, this.slidesPerPage);
    this.totalPages = Math.max(
      1,
      this.sliderItemsToShow.length - this.slidesPerPage + 1
    );
  }

  clampPage(page) {
    return Math.max(1, Math.min(page, this.totalPages || 1));
  }

  getPageScrollLeft(page) {
    if (!this.sliderItemsToShow.length) {
      return 0;
    }

    const safePage = this.clampPage(page);
    const maxScroll = Math.max(
      0,
      this.slider.scrollWidth - this.slider.clientWidth
    );

    if (safePage === 1 || this.totalPages <= 1) {
      return 0;
    }

    if (safePage === this.totalPages) {
      return maxScroll;
    }

    if (!this.sliderItemOffset) {
      return 0;
    }

    const targetLeft = (safePage - 1) * this.sliderItemOffset;

    return Math.max(0, Math.min(targetLeft, maxScroll));
  }

  getCurrentPageFromScroll() {
    if (!this.sliderItemsToShow.length || this.totalPages <= 1) {
      return 1;
    }

    let closestPage = 1;
    let closestDistance = Infinity;
    const scrollLeft = this.slider.scrollLeft;

    for (let page = 1; page <= this.totalPages; page++) {
      const pageScrollLeft = this.getPageScrollLeft(page);
      const distance = Math.abs(scrollLeft - pageScrollLeft);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestPage = page;
      }
    }

    return this.clampPage(closestPage);
  }

  isAtStart() {
    const tolerance = 5;
    return this.slider.scrollLeft <= tolerance;
  }

  isAtEnd() {
    const tolerance = 5;
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    return this.slider.scrollLeft >= maxScroll - tolerance;
  }

  scrollToPage(page, behavior = "smooth") {
    const safePage = this.clampPage(page);
    const left = this.getPageScrollLeft(safePage);

    this.currentPage = safePage;
    this.slideScrollPosition = left;

    this.slider.scrollTo({
      left,
      behavior,
    });
  }

  scrollToSlideByPage(page, behavior = "auto") {
    this.scrollToPage(page, behavior);
  }

  loopToStart() {
    if (this.isLooping) return;

    this.isLooping = true;
    this.scrollToPage(1, "auto");
    this.update();
    this.isLooping = false;
  }

  loopToEnd() {
    if (this.isLooping) return;

    this.isLooping = true;
    this.scrollToPage(this.totalPages, "auto");
    this.update();
    this.isLooping = false;
  }

  getButtonStep(target) {
    const step = parseInt(target?.dataset?.step || "1", 10);
    return Math.max(1, step);
  }

  getTargetPageFromDirection(direction, step = 1) {
    const currentPage = this.getCurrentPageFromScroll();

    if (direction === "next") {
      if (this.enableLooping) {
        return ((currentPage - 1 + step) % this.totalPages) + 1;
      }
      return this.clampPage(currentPage + step);
    }

    if (direction === "previous") {
      if (this.enableLooping) {
        return (
          ((currentPage - 1 - step + this.totalPages * 10) % this.totalPages) + 1
        );
      }
      return this.clampPage(currentPage - step);
    }

    return currentPage;
  }

  onButtonClick(event, autoplay = false) {
    if (!autoplay && event?.preventDefault) {
      event.preventDefault();
    }

    const target = event?.currentTarget || {};
    const direction = target.name;
    const step = this.getButtonStep(target);

    if (direction === "next" && this.enableLooping && this.isAtEnd()) {
      this.loopToStart();
      publish(PUB_SUB_EVENTS.carouselNavClicked, {
        direction: 1,
        totalPages: this.totalPages,
      });
      return;
    }

    if (direction === "previous" && this.enableLooping && this.isAtStart()) {
      this.loopToEnd();
      publish(PUB_SUB_EVENTS.carouselNavClicked, {
        direction: this.totalPages,
        totalPages: this.totalPages,
      });
      return;
    }

    const targetPage = this.getTargetPageFromDirection(direction, step);
    this.scrollToPage(targetPage, "smooth");

    publish(PUB_SUB_EVENTS.carouselNavClicked, {
      direction: targetPage,
      totalPages: this.totalPages,
    });
  }

  scrollToSlide(slideIndex, behavior = "auto") {
    if (slideIndex === undefined || slideIndex === null) return;

    const numericSlideIndex = Number(slideIndex);
    if (Number.isNaN(numericSlideIndex)) return;

    const page = this.clampPage(numericSlideIndex + 1);
    this.scrollToPage(page, behavior);
  }

  update() {
    if (this.isScrolling) return;

    this.isScrolling = true;

    window.requestAnimationFrame(() => {
      this.performUpdate();
      this.isScrolling = false;
    });
  }

  performUpdate() {
    if (!this.sliderItemsToShow.length) {
      this.currentPage = 1;
      this.updateButtonStates();
      this.updateTracker();
      return;
    }

    this.currentPage = this.getCurrentPageFromScroll();
    let currentSlide = this.currentPage;

    this.sliderItemsToShow.forEach((item, index) => {
      const slideNumber = index + 1;

      if (this.centered) {
        item.classList.remove("active");
      }

      if (this.isSlideVisible(item) && slideNumber < currentSlide) {
        currentSlide = slideNumber;
      }
    });

    if (this.centered) {
      if (currentSlide >= 0 && this.currentPage > 1) {
        if (
          this.totalPages < this.sliderItemsToShow.length &&
          this.sliderItemsToShow[currentSlide] &&
          window.innerWidth >= 1600
        ) {
          this.sliderItemsToShow[currentSlide].classList.add("active");
        } else if (this.sliderItemsToShow[currentSlide - 1]) {
          this.sliderItemsToShow[currentSlide - 1].classList.add("active");
        }
      } else if (this.sliderItemsToShow[0]) {
        this.sliderItemsToShow[0].classList.add("active");
      }
    }

    if (this.dots) {
      this.updateNextDot(currentSlide);
      this.updateDots(currentSlide);
    }

    this.updateButtonStates();
    this.updateTracker();
  }

  updateButtonStates() {
    if (this.prevButtons.length) {
      const shouldDisable =
        !this.enableLooping &&
        this.isSlideVisible(this.sliderItemsToShow[0]) &&
        !(this.centered && this.currentPage > 1);

      this.prevButtons.forEach((button) => {
        if (shouldDisable) {
          button.setAttribute("disabled", "disabled");
        } else {
          button.removeAttribute("disabled");
        }
      });
    }

    if (this.nextButtons.length) {
      const lastItem = this.sliderItemsToShow[this.sliderItemsToShow.length - 1];
      const shouldDisable =
        !this.enableLooping &&
        lastItem &&
        this.isSlideVisible(lastItem) &&
        !(this.centered && !lastItem.classList.contains("active"));

      this.nextButtons.forEach((button) => {
        if (shouldDisable) {
          button.setAttribute("disabled", "disabled");
        } else {
          button.removeAttribute("disabled");
        }
      });
    }
  }

  isSlideVisible(element, offset = 0) {
    if (!element) return false;

    const lastVisibleSlide = Math.ceil(
      this.slider.clientWidth + this.slider.scrollLeft - offset
    );

    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  initDots() {
    const allDots = this.dots.querySelectorAll("span");

    for (let index = 0; index < allDots.length; index++) {
      if (index >= this.totalPages) {
        allDots[index].style.display = "none";
      } else if (
        index === this.totalPages - 1 &&
        window.innerWidth > 2000 &&
        this.classList.contains("carousel--show-to-edge")
      ) {
        allDots[index].style.display = "none";
      } else {
        allDots[index].style.display = "block";
      }
    }
  }

  updateNextDot(slide) {
    const allDots = this.dots.querySelectorAll("span");

    allDots.forEach((dot) => {
      dot.classList.remove("next");
    });

    if (allDots[slide - 1]) {
      allDots[slide - 1].classList.add("next");
    }
  }

  updateDots(slide) {
    const allDots = this.dots.querySelectorAll("span");

    allDots.forEach((dot) => {
      dot.classList.remove("active");
    });

    if (allDots[slide - 1]) {
      allDots[slide - 1].classList.add("active");
    }
  }

  updateTracker() {
    if (!this.trackers.length) return;

    const scrollLeft = this.slider.scrollLeft;
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;

    if (maxScroll <= 0) {
      this.trackers.forEach((tracker) => {
        tracker.style.width = "100%";
      });
      return;
    }

    let percentage = (scrollLeft / maxScroll) * 100;
    percentage = Math.max(0, Math.min(percentage, 100));

    this.trackers.forEach((tracker) => {
      tracker.style.width = percentage + "%";
    });
  }

  autoplay() {
    this.autoplayInterval = setInterval(() => {
      this.onButtonClick(
        {
          currentTarget: {
            name: "next",
            dataset: {
              step: "1",
            },
          },
        },
        true
      );
    }, this.autoplaySpeed);
  }

  disconnectedCallback() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
    }

    this.cancelScrollEndDetection();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.slider) {
      this.slider.removeEventListener("scroll", this.boundUpdate);
      this.slider.removeEventListener("touchstart", this.boundTouchStart);
      this.slider.removeEventListener("touchend", this.boundTouchEnd);
    }

    if (this.prevButtons.length) {
      this.prevButtons.forEach((button) => {
        button.removeEventListener("click", this.boundButtonClick);
      });
    }

    if (this.nextButtons.length) {
      this.nextButtons.forEach((button) => {
        button.removeEventListener("click", this.boundButtonClick);
      });
    }

    if (
      this.thumbnailSubscription &&
      typeof this.thumbnailSubscription === "function"
    ) {
      this.thumbnailSubscription();
    }
  }
}

customElements.define("carousel-slider", Carousel);
